// InactivityWarning.js - Fixed with proper theme support
import React, { useEffect, useState } from 'react';
import { useAuth } from './AuthContext';
import { 
  Dialog, 
  DialogTitle, 
  DialogContent, 
  DialogActions, 
  Button, 
  Typography,
  Box,
  LinearProgress,
  useTheme
} from '@mui/material';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';

const InactivityWarning = () => {
  const { inactivityWarning, extendSession, logout, isAuthenticated } = useAuth();
  const [countdown, setCountdown] = useState(0);
  const theme = useTheme();

  useEffect(() => {
    // CRITICAL: Only run when authenticated
    if (!isAuthenticated) {
      setCountdown(0);
      return;
    }

    if (inactivityWarning) {
      setCountdown(inactivityWarning);
      
      // Start countdown
      const timer = setInterval(() => {
        setCountdown(prev => {
          if (prev <= 1) {
            clearInterval(timer);
            // Double-check authentication before logout
            if (isAuthenticated) {
              logout();
            }
            return 0;
          }
          return prev - 1;
        });
      }, 60000); // Update every minute (60000ms)

      return () => clearInterval(timer);
    } else {
      setCountdown(0);
    }
  }, [inactivityWarning, logout, isAuthenticated]);

  // Don't render if not authenticated or no warning
  if (!isAuthenticated || !inactivityWarning || countdown === 0) {
    return null;
  }

  const progress = (countdown / inactivityWarning) * 100;

  return (
    <Dialog
      open={true}
      onClose={extendSession}
      maxWidth="sm"
      fullWidth
      disableEscapeKeyDown={false}
      PaperProps={{
        sx: {
          borderRadius: 2,
          backgroundColor: theme.palette.background.paper,
        }
      }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box
            sx={{
              backgroundColor: theme.palette.mode === 'dark' 
                ? 'rgba(255, 152, 0, 0.2)' 
                : 'rgba(255, 152, 0, 0.1)',
              borderRadius: '50%',
              p: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <WarningAmberIcon 
              sx={{ 
                color: theme.palette.mode === 'dark' ? '#ffa726' : '#f57c00',
                fontSize: 28 
              }} 
            />
          </Box>
          <Typography 
            variant="h6" 
            component="div"
            sx={{ 
              fontWeight: 600,
              color: theme.palette.text.primary 
            }}
          >
            Session Timeout Warning
          </Typography>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ pt: 2, pb: 3 }}>
        <Typography 
          variant="body1" 
          sx={{ 
            mb: 3,
            color: theme.palette.text.primary 
          }}
        >
          You will be automatically logged out in{' '}
          <Typography 
            component="span" 
            sx={{ 
              fontWeight: 700,
              color: theme.palette.mode === 'dark' ? '#ffa726' : '#f57c00'
            }}
          >
            {countdown} minute{countdown !== 1 ? 's' : ''}
          </Typography>
          {' '}due to inactivity.
        </Typography>

        <Box sx={{ mb: 2 }}>
          <LinearProgress 
            variant="determinate" 
            value={progress}
            sx={{
              height: 8,
              borderRadius: 4,
              backgroundColor: theme.palette.mode === 'dark'
                ? 'rgba(255, 255, 255, 0.1)'
                : 'rgba(0, 0, 0, 0.1)',
              '& .MuiLinearProgress-bar': {
                borderRadius: 4,
                backgroundColor: theme.palette.mode === 'dark' ? '#ffa726' : '#f57c00',
              }
            }}
          />
        </Box>
        
        <Typography 
          variant="body2" 
          sx={{ 
            color: theme.palette.text.secondary,
            textAlign: 'center'
          }}
        >
          Click "Stay Logged In" to extend your session, or "Logout Now" to logout immediately.
        </Typography>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 3, gap: 1.5 }}>
        <Button
          onClick={logout}
          variant="outlined"
          color="inherit"
          sx={{
            flex: 1,
            textTransform: 'none',
            fontWeight: 500,
            borderColor: theme.palette.mode === 'dark' 
              ? 'rgba(255, 255, 255, 0.23)' 
              : 'rgba(0, 0, 0, 0.23)',
            color: theme.palette.text.primary,
            '&:hover': {
              borderColor: theme.palette.mode === 'dark' 
                ? 'rgba(255, 255, 255, 0.4)' 
                : 'rgba(0, 0, 0, 0.4)',
              backgroundColor: theme.palette.mode === 'dark'
                ? 'rgba(255, 255, 255, 0.05)'
                : 'rgba(0, 0, 0, 0.05)',
            }
          }}
        >
          Logout Now
        </Button>
        <Button
          onClick={extendSession}
          variant="contained"
          color="primary"
          autoFocus
          sx={{
            flex: 1,
            textTransform: 'none',
            fontWeight: 600,
            boxShadow: 2,
            '&:hover': {
              boxShadow: 4,
            }
          }}
        >
          Stay Logged In
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default InactivityWarning;